import { usePermission } from "@/hooks/usePermission";
import { useRecipeStockSettings } from "../hooks/useRecipeStockSettings";
import { RecipeStockSettingsFields } from "../components/RecipeStockSettingsFields";
import { PageHeading } from "@/components/ui/page";
import React, { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { ArrowLeft, Plus, Trash2 } from "lucide-react";
import { getApiErrorMessage, recipesAPI, productsAPI } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { useToast } from "@/hooks/use-toast";
import { Loading } from "@/components/ui/loader";
import { Switch } from "@/components/ui/switch";
import { useIsDirty, useUnsavedChangesGuard } from "@/hooks/useUnsavedChangesGuard";

const INGREDIENT_UNITS = [
	{ value: "piece", labelKey: "recipes.units.piece" },
	{ value: "gram", labelKey: "recipes.units.gram" },
	{ value: "kilogram", labelKey: "recipes.units.kilogram" },
	{ value: "milliliter", labelKey: "recipes.units.milliliter" },
	{ value: "liter", labelKey: "recipes.units.liter" },
] as const;

interface IngredientRow {
	productId: number;
	productName?: string;
	purchasePrice?: number;
	quantity: number;
	unit: string;
	wastePercent: number;
	isOptional: boolean;
	sortOrder: number;
}

interface ProductOption {
	id: number;
	name: string;
	purchasePrice: number;
	unit: string;
	type?: string;
}

const RecipeBuilderPage: React.FC = () => {
	const { t } = useTranslation();
	const { productId } = useParams<{ productId: string }>();
	const navigate = useNavigate();
	const { toast } = useToast();
	const [loading, setLoading] = useState(true);
	const [saving, setSaving] = useState(false);
	const [product, setProduct] = useState<{ id: number; name: string; sellingPrice: number } | null>(null);
	const [recipe, setRecipe] = useState<{
		id: number;
		menuProductId: number;
		name: string;
		ingredients: Array<{
			id?: number;
			productId: number;
			product?: { id: number; name: string; purchasePrice: number };
			quantity: number;
			unit: string;
			wastePercent: number;
			isOptional: boolean;
			sortOrder: number;
		}>;
	} | null>(null);
	const [ingredients, setIngredients] = useState<IngredientRow[]>([]);
	const { isDirty, markSaved } = useIsDirty({ recipe, ingredients }, !loading);
	const canReadStockSettings = usePermission(["settings.read", "recipes.read"]);
	const canUpdateStockSettings = usePermission(["settings.update", "recipes.update"]);
	const canEditRecipe = usePermission(recipe ? "recipes.update" : "recipes.create");
	const stockSettings = useRecipeStockSettings(!!product && canReadStockSettings);
	const { allowNavigation } = useUnsavedChangesGuard((isDirty || stockSettings.dirty) && !saving);
	const [products, setProducts] = useState<ProductOption[]>([]);

	const pid = productId ? parseInt(productId, 10) : NaN;

	useEffect(() => {
		if (Number.isNaN(pid)) {
			setLoading(false);
			return;
		}
		let cancelled = false;
		(async () => {
			try {
				const [prodRes, recipeRes, productsRes] = await Promise.all([
					productsAPI.getById(pid),
					recipesAPI.getByProductId(pid).catch(() => null),
					productsAPI.getAll({ limit: 500 }),
				]);
				if (cancelled) return;
				if (prodRes.type !== "menu_item") {
					toast({ title: t("recipes.errors.mustBeMenuItem"), variant: "destructive" });
					// redirect to back to product detail page
					navigate(`/products/${pid}`);
					return;
				}
				setProduct({ id: prodRes.id, name: prodRes.name, sellingPrice: Number(prodRes.sellingPrice) });
				const list = Array.isArray(productsRes?.products)
					? productsRes.products
					: Array.isArray(productsRes?.data)
						? productsRes.data
						: [];
				setProducts(
					list
						.filter((p: any) => p.type === "menu_item")
						.map((p: any) => ({
							id: p.id,
							name: p.name,
							purchasePrice: Number(p.purchasePrice ?? 0),
							unit: p.unit ?? "piece",
							type: p.type,
						})),
				);
				if (recipeRes && recipeRes.ingredients) {
					setRecipe(recipeRes);
					setIngredients(
						recipeRes.ingredients.map((ri: any, idx: number) => ({
							productId: ri.productId,
							productName: ri.product?.name,
							purchasePrice: ri.product?.purchasePrice,
							quantity: Number(ri.quantity),
							unit: ri.unit ?? "piece",
							wastePercent: Number(ri.wastePercent ?? 0),
							isOptional: Boolean(ri.isOptional),
							sortOrder: ri.sortOrder ?? idx,
						})),
					);
				} else {
					setRecipe(null);
					setIngredients([]);
				}
			} catch (e) {
				toast({ title: t("recipes.errors.loadFailed"), variant: "destructive" });
			} finally {
				if (!cancelled) setLoading(false);
			}
		})();
		return () => {
			cancelled = true;
		};
	}, [pid, t, toast, navigate]);

	const costPerUnit =
		ingredients.reduce((sum, row) => {
			const qty = row.quantity * (1 + (row.wastePercent || 0) / 100);
			const price = row.purchasePrice ?? products.find(p => p.id === row.productId)?.purchasePrice ?? 0;
			return sum + qty * price;
		}, 0) ?? 0;
	const sellingPrice = product?.sellingPrice ?? 0;
	const margin = sellingPrice - costPerUnit;
	const marginPercent = sellingPrice > 0 ? (margin / sellingPrice) * 100 : 0;

	const addRow = () => {
		setIngredients(prev => [
			...prev,
			{
				productId: products[0]?.id ?? 0,
				quantity: 1,
				unit: "piece",
				wastePercent: 0,
				isOptional: false,
				sortOrder: prev.length,
			},
		]);
	};

	const removeRow = (index: number) => {
		setIngredients(prev => prev.filter((_, i) => i !== index));
	};

	const updateRow = (index: number, field: keyof IngredientRow, value: any) => {
		setIngredients(prev => {
			const next = [...prev];
			(next[index] as any)[field] = value;
			if (field === "productId") {
				const p = products.find(pr => pr.id === value);
				if (p) {
					next[index].productName = p.name;
					next[index].purchasePrice = p.purchasePrice;
				}
			}
			return next;
		});
	};

	const handleSave = async () => {
		if (Number.isNaN(pid) || !product || saving) return;
		const saveIngredients = canEditRecipe && (isDirty || (!recipe && !stockSettings.dirty));
		if (!saveIngredients && !(canUpdateStockSettings && stockSettings.dirty)) return;
		setSaving(true);
		try {
			const payload = {
				menuProductId: pid,
				name: product.name,
				ingredients: ingredients.map((row, i) => ({
					productId: row.productId,
					quantity: row.quantity,
					unit: row.unit,
					wastePercent: row.wastePercent,
					isOptional: row.isOptional,
					sortOrder: i,
				})),
			};
			if (saveIngredients) {
				if (recipe) await recipesAPI.update(recipe.id, payload);
				else setRecipe(await recipesAPI.create(payload));
				markSaved();
			}
			if (canUpdateStockSettings && stockSettings.dirty) await stockSettings.save();
			toast({ title: t("recipes.messages.updated") });
			allowNavigation();
			navigate(`/products/${pid}`);
		} catch (e: any) {
			toast({
				title: t("recipes.errors.saveFailed"),
				description: getApiErrorMessage(e, t, ""),
				variant: "destructive",
			});
		} finally {
			setSaving(false);
		}
	};

	if (loading) return <Loading />;
	if (!productId || !product) {
		return (
			<div className="p-6">
				<Button variant="ghost" onClick={() => navigate("/products")}>
					<ArrowLeft className="rtl:rotate-180 h-4 w-4 me-2" />
					{t("common.back")}
				</Button>
				<p className="mt-4 text-muted-foreground">{t("recipes.errors.productNotFound")}</p>
			</div>
		);
	}

	return (
		<div className="container mx-auto max-w-full space-y-4 px-3 py-4 md:px-4 md:py-5">
			<div className="flex items-start gap-3">
				<div>
					<PageHeading className="font-semibold">{t("recipes.title")}</PageHeading>
					<p className="text-sm text-muted-foreground">{product.name}</p>
				</div>
			</div>

			<div className="grid gap-4 lg:grid-cols-12">
				<div className="lg:col-span-8 xl:col-span-9">
					<Card className="border-border/70">
						<CardHeader className="space-y-1 pb-4">
							<CardTitle className="text-base md:text-base">{t("recipes.ingredients")}</CardTitle>
							<CardDescription>{t("recipes.ingredientsDescription")}</CardDescription>
						</CardHeader>
						<CardContent className="space-y-3">
							{ingredients.map((row, index) => (
								<div key={index} className="rounded-lg border p-3 md:p-4">
									<div className="grid grid-cols-1 gap-3 md:grid-cols-6 xl:grid-cols-12 md:items-end">
										<div className="min-w-0 space-y-1 md:col-span-6 xl:col-span-3">
											<Label>{t("recipes.product")}</Label>
											<Select
												disabled={saving || !canEditRecipe}
												value={String(row.productId)}
												onValueChange={v => updateRow(index, "productId", parseInt(v, 10))}
											>
												<SelectTrigger aria-label={t("recipes.product")}>
													<SelectValue placeholder={t("recipes.selectProduct")} />
												</SelectTrigger>
												<SelectContent>
													{products.map(p => (
														<SelectItem key={p.id} value={String(p.id)}>
															{p.name}
														</SelectItem>
													))}
												</SelectContent>
											</Select>
										</div>

										<div className="min-w-0 space-y-1 md:col-span-2">
											<Label>{t("recipes.quantity")}</Label>
											<Input
												disabled={saving || !canEditRecipe}
												aria-label={t("recipes.quantity")}
												type="number"
												min={0}
												step="any"
												value={row.quantity}
												onChange={e =>
													updateRow(index, "quantity", parseFloat(e.target.value) || 0)
												}
											/>
										</div>

										<div className="min-w-0 space-y-1 md:col-span-2">
											<Label>{t("recipes.unit")}</Label>
											<Select
												disabled={saving || !canEditRecipe}
												value={row.unit}
												onValueChange={v => updateRow(index, "unit", v)}
											>
												<SelectTrigger aria-label={t("recipes.unit")}>
													<SelectValue />
												</SelectTrigger>
												<SelectContent>
													{INGREDIENT_UNITS.map(u => (
														<SelectItem key={u.value} value={u.value}>
															{t(u.labelKey)}
														</SelectItem>
													))}
												</SelectContent>
											</Select>
										</div>

										<div className="min-w-0 space-y-1 md:col-span-2">
											<Label>{t("recipes.wastePercent")}</Label>
											<Input
												disabled={saving || !canEditRecipe}
												aria-label={t("recipes.wastePercent")}
												type="number"
												min={0}
												step="0.1"
												value={row.wastePercent}
												onChange={e =>
													updateRow(index, "wastePercent", parseFloat(e.target.value) || 0)
												}
											/>
										</div>

										<div className="min-w-0 space-y-1 md:col-span-5 xl:col-span-2">
											<Label>{t("recipes.optional")}</Label>
											<div className="flex min-h-11 items-center">
												<Switch
													disabled={saving || !canEditRecipe}
													aria-label={t("recipes.optional")}
													checked={row.isOptional}
													onCheckedChange={checked => updateRow(index, "isOptional", checked)}
												/>
											</div>
										</div>

										<div className="space-y-1 md:col-span-1 flex justify-center items-center">
											<Button
												type="button"
												variant="ghost"
												size="icon"
												disabled={saving || !canEditRecipe}
												onClick={() => removeRow(index)}
												className="h-11 w-11"
												title={t("common.delete")}
												aria-label={t("common.delete")}
											>
												<Trash2 className="h-6 w-6 text-destructive" />
											</Button>
										</div>
									</div>
								</div>
							))}

							<Button
								type="button"
								variant="outline"
								disabled={saving || !canEditRecipe}
								onClick={addRow}
								className="w-full"
							>
								<Plus className="me-2 h-4 w-4" />
								{t("recipes.addIngredient")}
							</Button>
							{canReadStockSettings && (
								<RecipeStockSettingsFields
									model={stockSettings}
									disabled={saving || !canUpdateStockSettings}
								/>
							)}
						</CardContent>
					</Card>
				</div>

				<div className="lg:col-span-4 xl:col-span-3">
					<Card className="border-border/70 lg:sticky lg:top-4">
						<CardHeader className="space-y-1 pb-4">
							<CardTitle className="text-base md:text-base">{t("recipes.costPreview")}</CardTitle>
							<CardDescription>{t("recipes.costPreviewDescription")}</CardDescription>
						</CardHeader>
						<CardContent className="space-y-3">
							<div className="rounded-lg border p-3">
								<Label className="text-xs text-muted-foreground">{t("recipes.costPerUnit")}</Label>
								<p className="text-xl font-semibold">{costPerUnit.toFixed(2)}</p>
							</div>
							<div className="rounded-lg border p-3">
								<Label className="text-xs text-muted-foreground">{t("recipes.sellingPrice")}</Label>
								<p className="text-xl font-semibold">{sellingPrice.toFixed(2)}</p>
							</div>
							<div className="rounded-lg border p-3">
								<Label className="text-xs text-muted-foreground">{t("recipes.margin")}</Label>
								<p className="text-xl font-semibold">
									{margin.toFixed(2)} ({marginPercent.toFixed(1)}%)
								</p>
							</div>
							<Button
								className="w-full"
								onClick={handleSave}
								disabled={
									saving ||
									(canEditRecipe && isDirty && ingredients.some(i => !i.productId)) ||
									!(
										(canEditRecipe && (isDirty || !recipe)) ||
										(canUpdateStockSettings && stockSettings.dirty)
									)
								}
							>
								{saving ? t("common.saving") : t("common.save")}
							</Button>
						</CardContent>
					</Card>
				</div>
			</div>
		</div>
	);
};

export default RecipeBuilderPage;
